import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import type { Avatar as AvatarT } from '@/api/types';
import { Logo } from '@/components/ui/Logo';
import { Avatar } from '@/components/ui/Avatar';
import { Icon } from '@/components/ui/Icon';
import { Sheet } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { FloatIn, Mascot, PRESS, SpeechBubble } from '@/components/fun';
import { cn } from '@/lib/cn';
import { landingBeacon, tryStep, watchEngagement } from '@/lib/beacon';
import { SignUpBox } from '@/features/auth/Login';

/**
 * The landing page (docs/DESIGN.md, v11 "Poster"): a Popin date is a poster
 * stuck on a wall in your street. Chapters of flat colour meet edge to edge:
 * the red hero with two blobs stuck over the seam, a this-or-that you can
 * play right here, the three ways to Popin as panels, and the safety promise
 * on the black field. Under three phone screens.
 */

/** "Try it right here": a no-login this-or-that that answers back (client only). */
export const TRY: { q: string; a: [TryOption, TryOption] }[] = [
  {
    q: 'Chai or coffee?',
    a: [
      { label: 'Chai', reply: 'Chai gang! Kadak choice.' },
      { label: 'Coffee', reply: 'Coffee person! Bold. We like it.' },
    ],
  },
  {
    q: 'Mountains or beach?',
    a: [
      { label: 'Mountains', reply: 'Pahadi soul! Bring a jacket.' },
      { label: 'Beach', reply: 'Beach baby! Sunset dates it is.' },
    ],
  },
  {
    q: 'Texting or a call?',
    a: [
      { label: 'Texting', reply: 'Texter! Our games do the talking.' },
      { label: 'A call', reply: 'Caller! You’ll love video dates.' },
    ],
  },
  {
    q: 'Plan it or wing it?',
    a: [
      { label: 'Plan it', reply: 'Planner! Date links are your thing.' },
      { label: 'Wing it', reply: 'Spontaneous! Meet someone now.' },
    ],
  },
];
interface TryOption {
  label: string;
  reply: string;
}

const WRAP = 'mx-auto w-full max-w-content px-4 sm:px-6';
const FOCUS = 'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2';

/** The two blobs stuck on the hero: blue and yellow on the red field. */
const HERO_PAIR: [AvatarT, AvatarT] = [
  { hue: 220, shape: 'flower', face: 'grin' },
  { hue: 50, shape: 'blob', face: 'wink' },
];
const TRY_BLOB: AvatarT = { hue: 130, shape: 'star', face: 'smile' };

/** A blob slapped on like a paper sticker: lands once at its tilt, then rests. */
function StuckOn({ avatar, size, rotate, delay, className }: { avatar: AvatarT; size: number; rotate: number; delay: number; className?: string }) {
  const reduced = useReducedMotion();
  return (
    <motion.span
      className={cn('inline-block', className)}
      initial={reduced ? { opacity: 0, rotate } : { opacity: 0, scale: 1.18, rotate: rotate + 10 }}
      animate={{ opacity: 1, scale: 1, rotate }}
      transition={{ duration: reduced ? 0.15 : 0.35, ease: 'easeOut', delay: reduced ? 0 : delay }}
    >
      <Avatar avatar={avatar} size={size} sticker />
    </motion.span>
  );
}

/** The two characters, overlapping each other and the seam. Sizes follow the viewport. */
function HeroPair({ testId }: { testId?: string }) {
  return (
    <div className="flex items-end" data-testid={testId} role="img" aria-label="Two Popin characters, stuck on like stickers">
      <StuckOn avatar={HERO_PAIR[0]} size={156} rotate={-5} delay={0.25} className="relative z-[1] sm:hidden" />
      <StuckOn avatar={HERO_PAIR[1]} size={124} rotate={6} delay={0.4} className="-ml-10 mb-2 sm:hidden" />
      <StuckOn avatar={HERO_PAIR[0]} size={248} rotate={-5} delay={0.25} className="relative z-[1] hidden sm:inline-block lg:hidden" />
      <StuckOn avatar={HERO_PAIR[1]} size={196} rotate={6} delay={0.4} className="-ml-16 mb-4 hidden sm:inline-block lg:hidden" />
      <StuckOn avatar={HERO_PAIR[0]} size={300} rotate={-5} delay={0.25} className="relative z-[1] hidden lg:inline-block" />
      <StuckOn avatar={HERO_PAIR[1]} size={236} rotate={6} delay={0.4} className="-ml-20 mb-6 hidden lg:inline-block" />
    </div>
  );
}

function Hero({ onLink }: { onLink: () => void }) {
  return (
    <section className="relative" aria-labelledby="hero-title" data-testid="landing-hero">
      <div className="relative bg-red text-night">
        <header className={cn(WRAP, 'flex items-center justify-between py-4')}>
          <Logo onField />
          <Link to="/login" className={cn('inline-flex h-11 items-center rounded-full px-4 text-[15px] font-semibold hover:bg-night/10', FOCUS, 'focus-visible:outline-night')} data-testid="nav-login">
            Sign in
          </Link>
        </header>
        <div className={cn(WRAP, 'pb-0 pt-10 sm:pt-16 lg:pb-28 lg:pt-20')}>
          <FloatIn>
            <h1 id="hero-title" className="font-display text-[clamp(44px,14.6vw,56px)] leading-[0.88] [text-wrap:nowrap] sm:text-[80px] lg:text-[88px]">
              <span className="block">Dates that skip</span> <span className="block">the small talk.</span>
            </h1>
          </FloatIn>
          {/* Phones: the pair sits under the type, pushed right, straddling the seam. */}
          <div className="relative z-10 -mb-16 -mt-3 flex justify-end pr-1 sm:-mb-24 lg:hidden">
            <HeroPair testId="landing-chat" />
          </div>
        </div>
        {/* Desktop: right of the headline, its lower half on the black band. */}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 hidden lg:block">
          <div className={cn(WRAP, 'flex translate-y-[44%] justify-end pr-4')}>
            <HeroPair />
          </div>
        </div>
      </div>
      <div className="bg-night text-bone">
        <div className={cn(WRAP, 'pb-10 pt-[88px] sm:pt-32 lg:pb-16 lg:pt-14')}>
          <FloatIn i={1} className="lg:max-w-[40ch]">
            <p className="text-[20px] font-medium leading-snug" data-testid="landing-positioning">
              Matched on Hinge or Bumble? Send them a guided 30-minute video date. Meet first, share your number later.
            </p>
            <p className="mt-2 text-[15px] text-bone/70">Free · 18+</p>
          </FloatIn>
          {/* The main door: plan a date as a guest (docs/SPEC_TRY.md); sign-in only to send it. */}
          <FloatIn i={2} className="mt-7 lg:mt-8">
            <TryLink where="app_hero" />
          </FloatIn>
          {/* Or sign up right here: one tap with Google (One Tap where Chrome offers it). */}
          <FloatIn i={3} className="mt-5 flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:gap-4">
            <SignUpBox oneTap onDark className="w-full max-w-[360px]" />
            <button
              type="button"
              onClick={onLink}
              className={cn('h-12 rounded-full px-4 text-[15px] font-semibold text-bone underline decoration-bone/40 underline-offset-4 hover:decoration-bone', FOCUS, 'focus-visible:outline-bone')}
              data-testid="landing-have-link"
            >
              Got a date link?
            </button>
          </FloatIn>
        </div>
      </div>
    </section>
  );
}

/** "Plan a date": the /try flow (docs/SPEC_TRY.md). */
function TryLink({ where, dark = false }: { where: string; dark?: boolean }) {
  return (
    <Link
      to={`/try${location.search}`}
      onClick={() => tryStep('landing_cta', where)}
      className={cn(
        'inline-flex h-14 items-center gap-2 rounded-full px-7 text-[17px] font-semibold transition-transform active:scale-[0.97]',
        dark ? 'bg-night text-bone' : 'bg-green text-night',
        FOCUS,
        dark ? 'focus-visible:outline-night' : 'focus-visible:outline-bone',
      )}
      data-testid={`landing-plan-${where}`}
    >
      Plan a date <Icon name="chevron-right" size={20} />
    </Link>
  );
}

/** "Try it right here": the question small, the two answers loud. Tap one, a blob answers back. */
function TryIt() {
  const [round, setRound] = useState(0);
  const [picked, setPicked] = useState<number | null>(null);
  const t = TRY[round % TRY.length]!;
  const choice = picked === null ? null : t.a[picked]!;
  return (
    <section className="py-12 lg:py-20" aria-labelledby="try-title" data-testid="landing-try">
      <div className={WRAP}>
        <div className="flex items-end justify-between gap-4">
          <div>
            <h2 id="try-title" className="text-[22px] font-semibold leading-tight" data-testid="landing-try-q">
              {t.q}
            </h2>
            <p className="mt-1 text-[15px] text-muted">Pick one. No sign-in.</p>
          </div>
        </div>
        <div className="mt-5 grid grid-cols-2" role="group" aria-label={t.q}>
          {t.a.map((o, i) => {
            const on = picked === i;
            return (
              <motion.button
                key={o.label}
                type="button"
                {...PRESS}
                onClick={() => setPicked(i)}
                aria-pressed={on}
                className={cn(
                  'relative flex h-[132px] items-end overflow-hidden p-4 text-left transition-colors duration-150 sm:h-[180px] sm:p-6 lg:h-[220px] lg:p-8',
                  FOCUS,
                  'focus-visible:outline-fg focus-visible:z-10',
                  i === 0 ? 'rounded-l-card' : 'rounded-r-card',
                  on ? 'bg-green text-night' : i === 0 ? 'bg-surface hover:bg-raised' : 'bg-raised hover:bg-line',
                )}
                data-testid={`landing-try-${i}`}
              >
                <span className="font-display text-[clamp(30px,9.4vw,44px)] leading-[0.9] sm:text-[64px] lg:text-[88px]">{o.label}</span>
                {on && <Icon name="check" size={24} strokeWidth={2.6} className="absolute right-4 top-4 sm:right-6 sm:top-6" />}
              </motion.button>
            );
          })}
        </div>
        <div className="mt-4 flex min-h-[64px] flex-wrap items-center justify-between gap-3">
          {choice ? (
            <>
              <div className="flex min-w-0 items-end gap-2">
                <Mascot avatar={TRY_BLOB} size={52} mood="cheer" />
                <SpeechBubble side="left" tone="paper" bubbleKey={`${round}-${picked}`} live testId="landing-try-reply" className="mb-3 min-w-0">
                  {choice.reply}
                </SpeechBubble>
              </div>
              <button
                type="button"
                onClick={() => {
                  setRound((r) => r + 1);
                  setPicked(null);
                }}
                className={cn('inline-flex h-11 items-center gap-1 rounded-full bg-raised px-4 text-[15px] font-semibold hover:bg-line', FOCUS, 'focus-visible:outline-fg')}
                data-testid="landing-try-next"
              >
                Another one <Icon name="chevron-right" size={18} />
              </button>
            </>
          ) : (
            <div className="flex items-end gap-2 text-muted">
              <Mascot avatar={TRY_BLOB} size={52} />
              <p className="mb-3 text-[15px]">Go on, it’s not a test.</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

/** Three ways to Popin: panels of unequal width meeting edge to edge, each with a scrap of the real product. */
function Ways() {
  return (
    <section aria-labelledby="cards-title" data-testid="landing-cards">
      <div className={WRAP}>
        <h2 id="cards-title" className="text-[22px] font-semibold leading-tight">
          Three ways to Popin
        </h2>
      </div>
      <div className="mt-4 grid lg:grid-cols-[5fr_4fr_3.4fr]">
        <article className="flex flex-col bg-yellow px-4 pb-8 pt-7 text-night sm:px-6 lg:min-h-[380px] lg:px-10 lg:py-12 lg:pl-[max(24px,calc((100vw-1120px)/2+24px))]" data-testid="landing-card-dates">
          <h3 className="font-display text-[44px] leading-[0.9] lg:text-[56px]">Send a date link</h3>
          <p className="mt-3 max-w-[32ch] text-[17px]">They tap yes. You meet on video.</p>
          <p data-ui className="mt-6 inline-flex h-11 -rotate-3 self-start items-center gap-2 rounded-full bg-bone px-4 text-[15px] font-semibold">
            <Icon name="link" size={18} /> popin.buzz/@you
          </p>
          <p className="mt-6 pt-0 text-[15px] font-semibold lg:mt-auto lg:pt-8">Popin Dates</p>
        </article>
        <article className="flex flex-col bg-red px-4 pb-8 pt-7 text-night sm:px-6 lg:min-h-[380px] lg:px-10 lg:py-12" data-testid="landing-card-live">
          <h3 className="font-display text-[44px] leading-[0.9] lg:text-[56px]">Meet someone now</h3>
          <p className="mt-3 max-w-[32ch] text-[17px]">One tap, a new face, an opener ready.</p>
          <div data-ui className="mt-6 w-fit max-w-full rotate-2 self-start rounded-card bg-bone px-4 py-3">
            <p className="text-[13px] font-medium text-night/70">Your opener</p>
            <p className="text-[17px] font-semibold">Best street food in your city?</p>
          </div>
          <p className="mt-6 pt-0 text-[15px] font-semibold lg:mt-auto lg:pt-8">Popin Live</p>
        </article>
        <article className="flex flex-col bg-surface px-4 pb-10 pt-7 sm:px-6 lg:min-h-[380px] lg:px-10 lg:py-12 lg:pr-10" data-testid="landing-card-play">
          <h3 className="font-display text-[44px] leading-[0.9] lg:text-[56px]">Play while you talk</h3>
          <p className="mt-3 max-w-[32ch] text-[17px] text-muted">Quick games, so nobody runs out of words.</p>
          <ul data-ui className="mt-6 flex flex-wrap gap-2" aria-label="Some of the games">
            {['Two Truths & a Lie', 'Same Page', 'Doodle Dash', 'Hot Takes'].map((g) => (
              <li key={g} className="rounded-full bg-raised px-3.5 py-2 text-[15px] font-medium">
                {g}
              </li>
            ))}
          </ul>
          <p className="mt-6 text-[15px] font-semibold lg:mt-auto lg:pt-8">In every call</p>
        </article>
      </div>
    </section>
  );
}

const TRUST: { label: string; line: string }[] = [
  { label: 'Face-checked', line: 'A real face on camera, a real Google account.' },
  { label: 'Never recorded', line: 'Calls aren’t saved. Skip or report in one tap.' },
  { label: '18+ only', line: 'Adults only. Be kind, be real.' },
];

/** The promise, inverted onto the black field. */
function Trust({ onHow }: { onHow: () => void }) {
  return (
    <section className="bg-night text-bone" aria-labelledby="trust-title" data-testid="landing-trust">
      <div className={cn(WRAP, 'grid gap-10 py-14 lg:grid-cols-2 lg:gap-16 lg:py-24')}>
        <div>
          <h2 id="trust-title" className="font-display text-[clamp(44px,14vw,56px)] leading-[0.88] lg:text-[88px]">
            <span className="block">Meet first.</span> <span className="block">Share later.</span>
          </h2>
          <div className="mt-8 flex flex-wrap items-center gap-x-2 gap-y-1">
            <button
              type="button"
              onClick={onHow}
              className={cn('inline-flex h-12 items-center rounded-full bg-bone px-6 text-[15px] font-semibold text-night hover:bg-bone/90', FOCUS, 'focus-visible:outline-bone')}
              data-testid="landing-how"
            >
              How it works
            </button>
            <Link to="/safety" className={cn('inline-flex h-12 items-center gap-1 rounded-full px-4 text-[15px] font-semibold underline decoration-bone/40 underline-offset-4 hover:decoration-bone', FOCUS, 'focus-visible:outline-bone')}>
              Safety Center
            </Link>
          </div>
        </div>
        <ul className="space-y-6 lg:pt-3">
          {TRUST.map((t) => (
            <li key={t.label}>
              <p className="text-[22px] font-semibold leading-tight">{t.label}</p>
              <p className="mt-1 text-[15px] text-bone/70">{t.line}</p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/** The last screen: one more chance to sign up, with what happens next. */
function Closing() {
  return (
    <section className="bg-green text-night" aria-labelledby="closing-title" data-testid="landing-closing">
      <div className={cn(WRAP, 'grid gap-8 py-14 lg:grid-cols-2 lg:items-end lg:py-20')}>
        <div>
          <h2 id="closing-title" className="font-display text-[clamp(44px,14vw,56px)] leading-[0.88] lg:text-[80px]">
            <span className="block">Your first date</span> <span className="block">is one tap away.</span>
          </h2>
          <ol className="mt-6 space-y-2 text-[17px]">
            {['Plan the date.', 'Send them the link.', 'Meet on a guided video date.'].map((t, i) => (
              <li key={t} className="flex gap-3">
                <span className="font-display text-[22px] leading-none">{i + 1}</span>
                {t}
              </li>
            ))}
          </ol>
        </div>
        <div className="space-y-4">
          <TryLink where="app_close" dark />
          <SignUpBox className="w-full max-w-[360px]" />
        </div>
      </div>
    </section>
  );
}

/** Pull the invite token or @handle out of whatever they pasted. */
export function parseDateLink(raw: string): string | null {
  const s = raw.trim();
  if (!s) return null;
  const d = s.match(/(?:^|\/)d\/([A-Za-z0-9_-]{8,})/);
  if (d) return `/d/${d[1]}`;
  const h = s.match(/(?:^|\/|\s)@([a-z0-9._]{3,20})\b/i);
  if (h) return `/@${h[1]!.toLowerCase()}`;
  if (/^[A-Za-z0-9_-]{16,}$/.test(s)) return `/d/${s}`;
  return null;
}

function HaveLinkSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const [v, setV] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const go = (e: FormEvent) => {
    e.preventDefault();
    const to = parseDateLink(v);
    if (!to) return setErr('Hmm, that doesn’t look like a Popin link.');
    onClose();
    navigate(to);
  };
  return (
    <Sheet open={open} onClose={onClose} title="Got a date link?" description="Paste it here. No app needed." testId="landing-link-sheet">
      <form onSubmit={go} className="space-y-3">
        <label htmlFor="landing-link" className="sr-only">
          Your date link
        </label>
        <input
          id="landing-link"
          value={v}
          onChange={(e) => {
            setV(e.target.value);
            setErr(null);
          }}
          placeholder="popin.buzz/d/… or popin.buzz/@name"
          autoComplete="off"
          autoCapitalize="none"
          spellCheck={false}
          data-autofocus
          className="h-14 w-full rounded-full border border-line bg-ground px-5 text-[16px] text-fg placeholder:text-muted focus:outline-none focus-visible:border-fg"
          data-testid="landing-link-input"
        />
        {err && (
          <p role="alert" className="text-sm font-medium text-danger">
            {err}
          </p>
        )}
        <Button type="submit" size="lg" block iconRight="chevron-right" data-testid="landing-link-go">
          Open it
        </Button>
        <p className="text-[13px] text-muted">Usually someone sends it on WhatsApp or Insta. Tapping it works too.</p>
      </form>
    </Sheet>
  );
}

const HOW: { title: string; body: string }[] = [
  { title: 'Pick how', body: 'Send someone a date link, or meet someone new now.' },
  { title: 'Meet face to face', body: 'A guided video date. Openers, games, skip anything.' },
  { title: 'Keep the good ones', body: 'Both say yes? Now you’re friends. Chat, plan round two.' },
];

function HowSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <Sheet open={open} onClose={onClose} title="How it works" testId="landing-how-sheet">
      <ol className="mt-1 space-y-5">
        {HOW.map((h) => (
          <li key={h.title}>
            <p className="text-[17px] font-semibold">{h.title}</p>
            <p className="mt-0.5 text-[15px] text-muted">{h.body}</p>
          </li>
        ))}
      </ol>
      <div className="mt-6 rounded-card bg-raised p-4">
        <p className="text-[15px] font-semibold">Safe by design</p>
        <p className="mt-1 text-[15px] text-muted">Real Google accounts, 18+. Faces on camera, never recorded. Skip or report in one tap.</p>
        <Link to="/safety" onClick={onClose} className="mt-2 inline-flex h-11 items-center gap-1 text-[15px] font-semibold underline underline-offset-4">
          Safety Center <Icon name="chevron-right" size={16} />
        </Link>
      </div>
    </Sheet>
  );
}

export function Landing() {
  const [sheet, setSheet] = useState<'link' | 'how' | null>(null);
  useEffect(() => {
    landingBeacon('view');
    return watchEngagement();
  }, []);
  return (
    <div className="min-h-[100dvh] overflow-x-hidden bg-ground" data-testid="landing">
      <main>
        <Hero onLink={() => setSheet('link')} />
        <TryIt />
        <Ways />
        <Trust onHow={() => setSheet('how')} />
      </main>

      <Closing />

      <footer className={cn(WRAP, 'flex flex-col gap-4 py-8 text-[15px] text-muted sm:flex-row sm:items-center sm:justify-between')}>
        <Logo size={24} />
        <nav className="flex gap-5" aria-label="Footer">
          <Link to="/safety" className="hover:text-fg">
            Safety
          </Link>
          <Link to="/login" className="hover:text-fg">
            Sign in
          </Link>
          <Link to="/plus" className="hover:text-fg">
            Popin Plus
          </Link>
        </nav>
      </footer>

      <HaveLinkSheet open={sheet === 'link'} onClose={() => setSheet(null)} />
      <HowSheet open={sheet === 'how'} onClose={() => setSheet(null)} />
    </div>
  );
}
